import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const VendorDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [customerRatings, setCustomerRatings] = useState([]);
  const [serviceDetails, setServiceDetails] = useState({});
  const [customerNames, setCustomerNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [vendorInvoices, setVendorInvoices] = useState([]);
  const navigate = useNavigate();

  // Get user_id from localStorage instead of query params
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const user_id = user.id;

  useEffect(() => {
    if (!user_id) return;
    Promise.all([
  fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/vendor_appointments?user_id=${user_id}`).then(res => res.json()),
  fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/customer_ratings?vendor_id=${user_id}`).then(res => res.json()),
  fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/api/vendorinvoices?vendor_id=${user_id}`).then(res => res.json())
    ]).then(([appointmentsData, ratingsData, invoicesData]) => {
      setAppointments(appointmentsData.appointments || []);
      setCustomerRatings(ratingsData.ratings || []);
      setVendorInvoices(invoicesData.invoices || []);
      setLoading(false);
    });
  }, [user_id]);

  // Fetch service detail info for each appointment
  useEffect(() => {
    const fetchServiceDetails = async () => {
      const ids = [...new Set(appointments.map(app => app.product_detail_id).filter(Boolean))];
      const details = {};
      for (const id of ids) {
        try {
          const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/api/service_details_by_id/${id}`);
          const data = await res.json();
          if (data) {
            details[id] = {
              name: data[0].name || '',
              image_url: data[0].image_url || '',
            };
          }
        } catch {
          details[id] = { name: '', image_url: '' };
        }
      }
      setServiceDetails(details);
    };
    if (appointments.length > 0) fetchServiceDetails();
  }, [appointments]);

  // Fetch customer names for each appointment
  useEffect(() => {
    const fetchCustomerNames = async () => {
      const ids = [...new Set(appointments.map(app => app.customer_id).filter(Boolean))];
      const names = {};
      for (const id of ids) {
        try {
          const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/customer?customer_id=${id}`);
          const data = await res.json();
          if (data.customer) {
            names[id] = `${data.customer.firstName || ''} ${data.customer.lastName || ''}`.trim();
          }
        } catch {
          names[id] = '';
        }
      }
      setCustomerNames(names);
    };
    if (appointments.length > 0) fetchCustomerNames();
  }, [appointments]);

  if (loading) return <div style={styles.container}>Loading dashboard...</div>;

  // Filter only accepted appointments
  const acceptedAppointments = appointments.filter(app => app.status === 'accepted');

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Coach Dashboard</h2>
      <div style={styles.cardGrid}>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Accepted Appointments</h3>
          {acceptedAppointments.length === 0 ? (
            <div style={styles.empty}>No accepted appointments found.</div>
          ) : (
            <ul style={styles.list}>
              {acceptedAppointments.map(app => {
                const service = serviceDetails[app.product_detail_id] || {};
                const customerName = customerNames[app.customer_id] || app.customer_name || '';
                return (
                  <li key={app.id} style={{
                    ...styles.listItem,
                    background: '#e8f5e9',
                    borderLeft: '5px solid #388e3c',
                  }}>
                    {/* Service image and name on top */}
                    <div style={styles.serviceTopRow}>
                      {service.image_url ? (
                        <img
                          src={service.image_url}
                          alt={service.name || 'Service'}
                          style={styles.serviceImage}
                        />
                      ) : (
                        <div style={styles.noImage}>No Image</div>
                      )}
                      <span style={styles.serviceName}>{service.name}</span>
                    </div>
                    <div style={styles.rowTop}>
                      <div style={styles.dateTimeBlock}>
                        <div style={styles.dateTitle}>Date:</div>
                        <span style={styles.date}>
                          {app.date
                            ? (() => {
                                const d = new Date(app.date);
                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                const dd = String(d.getDate()).padStart(2, '0');
                                const yyyy = d.getFullYear();
                                return `${mm}/${dd}/${yyyy}`;
                              })()
                            : ''}
                        </span>
                        <div style={styles.timeTitle}>Start Time:</div>
                        <span style={styles.date}>{app.start_time || ''}</span>
                        <div style={styles.timeTitle}>End Time:</div>
                        <span style={styles.date}>{app.end_time || ''}</span>
                      </div>
                      <span style={{
                        ...styles.statusBadge,
                        background: '#388e3c',
                      }}>
                        Accepted
                      </span>
                    </div>
                    <div style={styles.rowBottom}>
                      <span style={styles.customer}>{customerName}</span>
                      {app.notes && (
                        <span style={styles.notes}>{app.notes}</span>
                      )}
                    </div>
                    {/* Add appointment links section */}
                    {app.appointment_links && app.appointment_links.trim() !== '' && (
                      <div style={styles.appointmentLinksRow}>
                        <span style={styles.linkLabel}>Conference Link:</span>
                        <div
                          style={styles.linkContent}
                          dangerouslySetInnerHTML={{ __html: app.appointment_links }}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {/* More appointments link */}
          {acceptedAppointments.length > 0 && (
            <div style={styles.moreLinkRow}>
              <button
                style={styles.moreLink}
                onClick={() => navigate('/vendorappointments')}
              >
                All Appointments
              </button>
            </div>
          )}
        </div>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Quick Stats</h3>
          <div style={styles.statsGrid}>
            <div
              style={{
                ...styles.statBox,
                cursor: appointments.length === 0 ? 'default' : 'pointer',
                opacity: appointments.length === 0 ? 0.6 : 1,
              }}
              onClick={() => appointments.length > 0 && navigate('/vendorappointments')}
              role="button"
              tabIndex={0}
            >
              <span style={styles.statLabel}>Total Appointments</span>
              <span style={styles.statValue}>{appointments.length}</span>
            </div>
            <div
              style={{
                ...styles.statBox,
                cursor: appointments.filter(a => a.status === 'accepted').length === 0 ? 'default' : 'pointer',
                opacity: appointments.filter(a => a.status === 'accepted').length === 0 ? 0.6 : 1,
              }}
              onClick={() =>
                appointments.filter(a => a.status === 'accepted').length > 0 &&
                navigate('/vendorappointments?status=accepted')
              }
              role="button"
              tabIndex={0}
            >
              <span style={styles.statLabel}>Accepted</span>
              <span style={styles.statValue}>
                {appointments.filter(a => a.status === 'accepted').length}
              </span>
            </div>
            <div
              style={{
                ...styles.statBox,
                cursor: appointments.filter(a => a.status === 'pending').length === 0 ? 'default' : 'pointer',
                opacity: appointments.filter(a => a.status === 'pending').length === 0 ? 0.6 : 1,
              }}
              onClick={() =>
                appointments.filter(a => a.status === 'pending').length > 0 &&
                navigate('/vendorappointments?status=pending')
              }
              role="button"
              tabIndex={0}
            >
              <span style={styles.statLabel}>Pending</span>
              <span style={styles.statValue}>
                {appointments.filter(a => a.status === 'pending').length}
              </span>
            </div>
            <div
              style={{
                ...styles.statBox,
                cursor: appointments.filter(a => a.status === 'rejected').length === 0 ? 'default' : 'pointer',
                opacity: appointments.filter(a => a.status === 'rejected').length === 0 ? 0.6 : 1,
              }}
              onClick={() =>
                appointments.filter(a => a.status === 'rejected').length > 0 &&
                navigate('/vendorappointments?status=rejected')
              }
              role="button"
              tabIndex={0}
            >
              <span style={styles.statLabel}>Rejected</span>
              <span style={styles.statValue}>
                {appointments.filter(a => a.status === 'rejected').length}
              </span>
            </div>

               <div
              style={{
                ...styles.statBox,
                cursor: appointments.filter(a => a.status === 'completed').length === 0 ? 'default' : 'pointer',
                opacity: appointments.filter(a => a.status === 'completed').length === 0 ? 0.6 : 1,
              }}
              onClick={() =>
                appointments.filter(a => a.status === 'completed').length > 0 &&
                navigate('/vendorappointments?status=completed')
              }
              role="button"
              tabIndex={0}
            >
              <span style={styles.statLabel}>Completed</span>
              <span style={styles.statValue}>
                {appointments.filter(a => a.status === 'completed').length}
              </span>
            </div>
    <div
              style={{
                ...styles.statBox,
                cursor: appointments.filter(a => a.status === 'cancelled').length === 0 ? 'default' : 'pointer',
                opacity: appointments.filter(a => a.status === 'cancelled').length === 0 ? 0.6 : 1,
              }}
              onClick={() =>
                appointments.filter(a => a.status === 'cancelled').length > 0 &&
                navigate('/vendorappointments?status=cancelled')
              }
              role="button"
              tabIndex={0}
            >
              <span style={styles.statLabel}>Cancelled</span>
              <span style={styles.statValue}>
                {appointments.filter(a => a.status === 'cancelled').length}
              </span>
            </div>
          </div>
        </div>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Customer Ratings</h3>
          {customerRatings.length === 0 ? (
            <div style={styles.empty}>No ratings yet.</div>
          ) : (
            <ul style={styles.list}>
              {customerRatings.map(rating => (
                <li key={rating.id} style={styles.ratingItem}>
                  <span style={styles.stars}>
                    {'★'.repeat(rating.rating)}
                    {'☆'.repeat(5 - rating.rating)}
                  </span>
                  <span style={styles.ratingComment}>
                    {rating.comment}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Coaching Invoices</h3>
          {vendorInvoices.length === 0 ? (
            <div style={styles.empty}>No invoices found.</div>
          ) : (
            <ul style={styles.list}>
              {vendorInvoices.map(inv => (
                <li key={inv.id} style={styles.listItem}>
                  <div>
                    <strong>Invoice #: </strong>{inv.id}
                  </div>
                  <div>
                    <strong>Amount: </strong>${inv.amount}
                  </div>
                  <div>
                    <strong>Status: </strong>
                    <span style={{
                      color: inv.status === 'paid' ? '#388e3c' : '#d32f2f',
                      fontWeight: 'bold'
                    }}>
                      {inv.status}
                    </span>
                  </div>
                  <div>
                    <strong>Date: </strong>
                    {inv.creation_date
                      ? new Date(inv.creation_date).toLocaleDateString()
                      : ''}
                  </div>
                  <button
                    style={{
                      ...styles.moreLink,
                      padding: '6px 16px',
                      fontSize: '0.98rem',
                      marginTop: '8px'
                    }}
                    onClick={() => navigate(`/invoicedetails/${inv.id}`)}
                  >
                    View Details
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1100px',
    margin: '40px auto',
    padding: '32px',
    background: '#fff',
    borderRadius: '18px',
    boxShadow: '0 6px 32px rgba(141,110,54,0.10)',
  },
  title: {
    marginBottom: '38px',
    color: '#4078c0',
    fontSize: '2.2rem',
    textAlign: 'center',
    fontWeight: 'bold',
    letterSpacing: '1.5px',
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '36px',
  },
  card: {
    background: '#f5f6fa',
    borderRadius: '14px',
    boxShadow: '0 1px 8px #e0cfa0',
    padding: '28px 22px 22px 22px',
    minHeight: '220px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    border: '2px solid #e0cfa0',
    transition: 'box-shadow 0.2s, border 0.2s, background 0.2s',
    position: 'relative',
  },
  cardTitle: {
    marginBottom: '18px',
    color: '#8d6e36',
    fontSize: '1.18rem',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  empty: {
    color: '#888',
    fontSize: '1.08rem',
    textAlign: 'center',
    marginTop: '24px',
    fontStyle: 'italic',
  },
  list: {
    paddingLeft: '0',
    fontSize: '1rem',
    color: '#333',
    listStyle: 'none',
    margin: 0,
  },
  listItem: {
    marginBottom: '18px',
    padding: '18px 18px 12px 18px',
    borderRadius: '10px',
    boxShadow: '0 1px 4px #e0cfa0',
    fontFamily: 'Segoe UI, Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    borderLeft: '5px solid #e0cfa0',
    background: '#f5f6fa',
    position: 'relative',
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  dateTimeBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '120px',
  },
  dateTitle: {
    color: '#8d6e36',
    fontWeight: 'bold',
    fontSize: '0.98rem',
    marginBottom: '2px',
  },
  timeTitle: {
    color: '#8d6e36',
    fontWeight: 'bold',
    fontSize: '0.98rem',
    marginTop: '2px',
  },
  date: {
    color: '#4078c0',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    letterSpacing: '0.5px',
  },
  statusBadge: {
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '8px',
    padding: '4px 16px',
    fontSize: '0.98rem',
    letterSpacing: '1px',
    textAlign: 'center',
    boxShadow: '0 1px 4px #e0cfa0',
    border: '2px solid #fff3e0',
    marginLeft: '12px',
  },
  rowBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '6px',
    gap: '12px',
  },
  customer: {
    color: '#388e3c',
    fontWeight: 'bold',
    fontSize: '1.05rem',
    letterSpacing: '0.5px',
  },
  notes: {
    color: '#8d6e36',
    fontSize: '0.98rem',
    fontStyle: 'italic',
    textAlign: 'right',
    maxWidth: '60%',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '18px',
    marginTop: '8px',
  },
  statBox: {
    background: '#fffde4',
    borderRadius: '8px',
    padding: '18px 12px',
    textAlign: 'center',
    boxShadow: '0 1px 4px #e0cfa0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer', // Make it look clickable
    transition: 'background 0.2s, box-shadow 0.2s',
    outline: 'none',
  },
  statLabel: {
    color: '#8d6e36',
    fontWeight: 'bold',
    fontSize: '1.02rem',
    marginBottom: '2px',
  },
  statValue: {
    color: '#4078c0',
    fontWeight: 'bold',
    fontSize: '1.25rem',
    letterSpacing: '1px',
  },
  ratingItem: {
    marginBottom: '14px',
    padding: '10px 14px',
    borderRadius: '8px',
    background: '#fffde4',
    boxShadow: '0 1px 4px #e0cfa0',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stars: {
    color: '#FFD700',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    letterSpacing: '2px',
  },
  ratingComment: {
    color: '#333',
    fontSize: '1rem',
    fontStyle: 'italic',
  },
  moreLinkRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '18px',
  },
  moreLink: {
    background: '#4078c0',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 22px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #e0cfa0',
    transition: 'background 0.2s',
    textDecoration: 'none',
  },
  serviceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: '12px 0 0 0',
  },
  serviceImage: {
    width: '54px',
    height: '54px',
    objectFit: 'cover',
    borderRadius: '10px',
    boxShadow: '0 1px 4px #e0cfa0',
    background: '#fff',
    border: '1px solid #eee',
  },
  noImage: {
    width: '54px',
    height: '54px',
    borderRadius: '10px',
    background: '#eee',
    color: '#aaa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.95rem',
    boxShadow: '0 1px 4px #e0cfa0',
  },
  serviceName: {
    color: '#388e3c',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    textAlign: 'left',
    letterSpacing: '0.5px',
    fontFamily: 'Segoe UI, Arial, sans-serif',
  },
  serviceTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '10px',
    marginTop: '2px',
  },
  appointmentLinksRow: {
    marginTop: '12px',
    padding: '12px',
    background: '#e3f2fd',
    borderRadius: '8px',
    border: '1px solid #bbdefb',
  },
  linkLabel: {
    color: '#1976d2',
    fontWeight: 'bold',
    fontSize: '0.95rem',
    marginBottom: '6px',
    display: 'block',
  },
  linkContent: {
    color: '#388e3c',
    fontSize: '0.9rem',
    wordBreak: 'break-all',
    '& a': {
      color: '#388e3c',
      textDecoration: 'none',
      fontWeight: 'bold',
    },
    '& a:hover': {
      textDecoration: 'underline',
    },
  },
};

export default VendorDashboard;
